import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';
import type { AgentRun } from '@/redux/internal-services/agentApi';

// ── AI provider billing (raw provider responses + usage recorded by this app) ──────────

export type AiBillingRange = 'today' | '7d' | 'month' | 'custom';

export interface AiBillingArgs {
  range: AiBillingRange;
  date_from?: string;
  date_to?: string;
  refresh?: boolean;
}

export interface AiBillingRawCall {
  name: string;
  doc: string;
  method: string;
  url: string;
  authenticated_with: string;
  status: number | null;
  ok: boolean;
  body: string;
  truncated: boolean;
  elapsed_ms: number;
  fetched_at: string;
  request_id?: string | null;
}

export interface AiBillingMetric {
  status: string;
  label: string;
  reason: string;
  requires: { credential: string; env: string; how: string };
}

export interface AiBillingTotals {
  runs: number; requests: number; input_tokens: number; output_tokens: number; cached_tokens: number;
  reasoning_tokens: number; audio_input_tokens: number; audio_output_tokens: number;
  text_input_tokens: number; text_output_tokens: number; billable_audio_seconds: number;
}

export interface AiBillingProvider {
  provider: string;
  label: string;
  docs: string;
  connection: { status: string; detail: string; checked_at: string | null; account?: Record<string, unknown> };
  raw_calls: AiBillingRawCall[];
  billing_credential_configured: boolean;
  metrics: Record<string, AiBillingMetric>;
  synced_at: string | null;
  from_cache?: boolean;
  configured: {
    chat: { provider: string; model?: string | null } | null;
    realtime: { provider: string; model?: string | null } | null;
    agent_rules: number;
  } | null;
  recorded: (AiBillingTotals & { vendor: string; text: AiBillingTotals; voice: AiBillingTotals }) | null;
}

export interface AiBillingRecentRun {
  run_id: string; session_id: string; at: string | null; vendor: string; provider: string | null; model: string | null;
  mode: string | null; status: string | null; requests: number; input_tokens: number; output_tokens: number;
  cached_tokens: number; reasoning_tokens: number;
}

export interface AiBillingReport {
  range: { key: string; from: string; to: string };
  generated_at: string;
  providers: AiBillingProvider[];
  recorded: {
    totals: AiBillingTotals;
    by_model: Array<AiBillingTotals & { vendor: string; model: string }>;
    by_day: Array<AiBillingTotals & { date: string; vendor: string }>;
    text_vs_voice: { text: AiBillingTotals; voice: AiBillingTotals };
    recent_runs: AiBillingRecentRun[];
    truncated: boolean;
  };
  limits: { probe_ttl_s: number; refresh_floor_s: number };
}

// ── Usage types ────────────────────────────────────────────────────────────

export type UsageGroupBy =
  | 'total' | 'tenant' | 'user' | 'provider' | 'provider_model' | 'model' | 'agent' | 'mode' | 'day' | 'run';

export interface UsageFilters {
  tenant_id?: string;
  user_id?: string;
  provider?: string;
  model?: string;
  agent_id?: string;
  interaction_mode?: string;
  date_from?: string;
  date_to?: string;
}

export interface UsageTotals {
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  /** subset of output tokens spent on reasoning, as reported (0 for runs recorded before it was tracked) */
  reasoning_tokens?: number;
  /** model calls made (0 for runs recorded before it was tracked) */
  requests?: number;
  /** voice runs only, as the provider reported them */
  audio_input_tokens?: number;
  audio_output_tokens?: number;
  text_input_tokens?: number;
  text_output_tokens?: number;
  billable_audio_seconds?: number;
  image_input_tokens?: number;
  /** cached input per modality, as the provider reported it (voice only) */
  cached_text_input_tokens?: number;
  cached_audio_input_tokens?: number;
  cached_image_input_tokens?: number;
  /** estimated USD from the effective-dated price versions; unpriced_tokens = tokens of models with no price that day */
  estimated_cost_usd?: number;
  unpriced_tokens?: number;
  total_tokens: number;
  runs: number;
  errors: number;
  last_at?: string | null;
}

export interface UsageRow extends UsageTotals {
  key: string | null;
  label: string;
  tenant_id?: string | null;
  tenant_name?: string | null;
  email?: string | null;
  /** provider_model grouping, and vendor/model rows of a split grouping */
  provider?: string;
  model?: string;
  /** split=vendor_model only: entity (the grouping's own row) | vendor subtotal | model */
  level?: 'entity' | 'vendor' | 'model' | 'run';
  entity_key?: string | null;
  /** group_by=run only: one chat turn or voice session */
  run_id?: string;
  session_id?: string | null;
  conversation_id?: string | null;
  interaction_mode?: string | null;
  status?: string | null;
  duration_s?: number | null;
  user_name?: string | null;
}

export interface LlmPrice {
  price_id: string;
  vendor: string;
  model: string;
  effective_from: string;
  input_per_mtok: number;
  output_per_mtok: number;
  cached_input_per_mtok?: number | null;
  cache_write_per_mtok?: number | null;
  audio_input_per_mtok?: number | null;
  cached_audio_input_per_mtok?: number | null;
  image_input_per_mtok?: number | null;
  audio_output_per_mtok?: number | null;
  per_audio_second?: number | null;
  note?: string | null;
}

export type LlmPriceInput = Omit<LlmPrice, 'price_id'>;

export interface UsageResponse {
  group_by: UsageGroupBy;
  totals: UsageTotals;
  rows: UsageRow[];
}

// ── Platform tenant types ──────────────────────────────────────────────────

export interface PlatformTenant {
  tenant_id: string;
  company_name: string;
  lifecycle_status: string;
  plan_name?: string | null;
  plan_id?: string | null;
  user_count?: number;
  created_at?: string | null;
  /** Shown with the tenant id so two tenants with the same company name can be told apart. */
  address?: { street?: string | null; city?: string | null; state?: string | null; zip?: string | null; country?: string | null } | null;
  primary_contact?: { name?: string; email?: string; phone?: string } | null;
  subscription_status?: string | null;
  billing_cycle?: string | null;
}

export interface PlatformTenantDetail extends PlatformTenant {
  address?: { street?: string; city?: string; state?: string; zip?: string; country?: string } | null;
  scope_of_work?: string[];
  lifecycle_history?: Array<{ from: string; to: string; reason: string; timestamp: string }>;
  subscription?: {
    plan_id: string;
    plan_name?: string;
    status: string;
    billing_cycle: string;
    trial_start?: string | null;
    trial_end?: string | null;
    billing_start?: string | null;
  } | null;
}

// ── Platform user types ────────────────────────────────────────────────────

export interface PlatformUser {
  user_id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  tenant_id?: string | null;
  tenant_name?: string | null;
  role_names: string[];
  primary_role_key?: string | null;
  primary_role_name?: string | null;
  status: string;
  created_at?: string | null;
  joined_at?: string | null;
  profile_picture_url?: string | null;
}

// ── Clone tenant types ─────────────────────────────────────────────────────

export interface CloneUserMapping {
  source_user_id: string;
  name: string;
  email: string;
  password: string;
}

export interface CloneTenantRequest {
  company_name: string;
  users: CloneUserMapping[];
}

export interface CloneTenantResult {
  tenant_id: string;
  company_name: string;
}

export interface PlatformUserMembership {
  tenant_id: string;
  tenant_name?: string | null;
  tenant_role_ids: string[];
  role_names: string[];
  status: string;
  joined_at?: string | null;
}

export interface PlatformUserDetail {
  user_id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  profile_picture_url?: string | null;
  is_active?: boolean;
  email_verified?: boolean;
  created_at?: string | null;
  memberships: PlatformUserMembership[];
}

export interface CreatePlatformUserRequest {
  name: string;
  email: string;
  password: string;
  platform_role?: string | null;
  tenant_id?: string | null;
  tenant_role_ids?: string[];
}

export interface UpdatePlatformUserRequest {
  name?: string;
  email?: string;
  phone?: string;
}

export interface UpdateUserRolesRequest {
  platform_role?: string | null;
  tenant_id?: string | null;
  tenant_role_ids?: string[];
}

// ── Plan types ─────────────────────────────────────────────────────────────

export interface PlatformPlan {
  plan_id: string;
  name: string;
  description: string;
  price_monthly: number | null;
  price_annual: number | null;
  user_limit?: number | null;
  features?: string[];
  trial_eligible?: boolean;
  trial_duration_days?: number | null;
  is_active?: boolean;
  tenant_count?: number;
}

export interface CreatePlanRequest {
  name: string;
  description: string;
  price_monthly: number | null;
  price_annual: number | null;
  user_limit?: number | null;
  features?: string[];
  trial_eligible?: boolean;
  trial_duration_days?: number | null;
}

export interface UpdatePlanRequest {
  name?: string;
  description?: string;
  price_monthly?: number | null;
  price_annual?: number | null;
  user_limit?: number | null;
  features?: string[];
  trial_eligible?: boolean;
  trial_duration_days?: number | null;
  is_active?: boolean;
}

// ── Config catalogue types ─────────────────────────────────────────────────

export interface LayerOverride {
  role_id?: string | null;
  user_id?: string | null;
  tenant_id?: string | null;
  value: Record<string, unknown>;
}

export interface CatalogueEntry {
  key: string;
  description?: string;
  parent?: string | null;
  layers?: Record<string, { editable?: boolean }>;
  settings: Record<string, {
    type: string;
    default: unknown;
    description?: string;
    post_setup_editable?: boolean;
    validation?: { allowed?: string[]; min?: number; max?: number };
    layers?: Record<string, { visible?: boolean }>;
  }>;
  effective_value?: Record<string, unknown>;
  system_value?: Record<string, unknown>;
  tenant_value?: Record<string, unknown> | null;
  role_overrides?: LayerOverride[];
  user_overrides?: LayerOverride[];
}

// ── SAM prompt version types ─────────────────────────────────────────────────

export interface PromptVersion {
  key: string;
  setting: string;
  /** A prompt's text. (The SAM rulebook history stores the whole rulebook object here — read it
   *  through `RulebookVersion`.) */
  value: string;
  saved_at: string;
  saved_by: string;
}

/** GET /api/v1/configurations/{key} — one catalogue entry with every layer's value. */
export interface ConfigurationEntry {
  key: string;
  description?: string;
  settings?: Record<string, unknown>;
  effective_value: Record<string, unknown>;
  system_value: Record<string, unknown>;
  tenant_value?: Record<string, unknown>;
  /** Only on `sam_cards.presentation_policy`, for platform admins (cards/policy.py editor_schema). */
  editor_schema?: RulebookSchema;
}

export interface RulebookFactSchema { name: string; kind: 'enum' | 'bool' | 'int' | 'text' | 'card' | 'cards'; values?: string[] }
export interface RulebookOptionSchema {
  name: string;
  kind: 'enum' | 'bool' | 'toast' | 'cards' | 'object';
  default: unknown;
  values?: string[];
  /** Actions this option is valid with; null = any. */
  actions: string[] | null;
}
/** A saved SAM rulebook (version history entry, setting "policy"). */
export type RulebookVersion = Omit<PromptVersion, 'value'> & { value: Record<string, unknown> };

export interface RulebookSchema {
  facts: RulebookFactSchema[];
  operators: string[];
  actions: string[];
  options: RulebookOptionSchema[];
  cards: string[];
  toast: { levels: string[]; message_codes: number[]; duration_ms: { min: number; max: number; default: number } };
}

/** A registered agent that exposes an editable prompt catalogue (prompt-editor dropdown). */
export interface AgentPromptCatalogue {
  agent_type: string;
  label: string;
  prompt_catalogue_key: string;
}

// ── Chat Monitor types ─────────────────────────────────────────────────────

export interface ChatFeedbackEntry {
  feedback_id: string;
  session_id: string;
  message_id: string;
  user_id: string;
  tenant_id: string | null;
  helpful: boolean;
  comment: string | null;
  created_at: string;
  session_title?: string | null;
  agent_id?: string | null;
  user_name?: string | null;
  user_email?: string | null;
}

export interface PlatformChatMessage {
  message_id: string;
  role: 'user' | 'assistant';
  content: string;
  type?: string;
  metadata?: Record<string, unknown>;
  files?: unknown[];
  created_at?: string;
}

export interface PlatformConversation {
  session_id: string;
  conversation_id: string;
  user_id: string;
  user_name?: string | null;
  user_email?: string | null;
  agent_id: string;
  title: string;
  tenant_id: string | null;
  created_at?: string;
  last_activity_at?: string;
  messages: PlatformChatMessage[];
  has_more?: boolean;
  total_messages?: number;
  /** Keyed by message_id — present when that message received feedback. */
  feedback?: Record<string, ChatFeedbackEntry>;
}

// ── Agent session log types ───────────────────────────────────────────────────

export interface AgentSessionSummary {
  session_id: string;
  user_id: string;
  user_name?: string | null;
  tenant_id: string | null;
  agent_type: string;
  entry_count: number;
  turn_count: number;
  started_at: string;
  last_entry_at: string;
}

export interface SessionEntry {
  entry_type: 'tool_call' | 'tool_result' | 'assistant' | 'user' | 'outbound' | 'session_updated';
  timestamp: string;
  // tool_call / tool_result
  tool?: string;
  args?: string;
  result?: string;
  // assistant / user
  text?: string;
  // outbound
  kind?: string;
  // session_updated
  instr_len?: number;
  instructions?: string;
}

export interface AgentSessionDetail {
  session_id: string;
  user_id: string;
  user_name?: string | null;
  tenant_id: string | null;
  agent_type: string;
  entries: SessionEntry[];
}

export const platformAdminApi = createApi({
  reducerPath: 'platformAdminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PlatformTenant', 'PlatformUser', 'PlatformPlan', 'PlatformConfig', 'LlmPricing'],
  endpoints: (builder) => ({

    // ── Tenants ──────────────────────────────────────────────────────────────
    listPlatformTenants: builder.query<
      { success: boolean; data: PlatformTenant[]; total: number },
      { search?: string; status?: string; plan?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        const p = (params ?? {}) as { search?: string; status?: string; plan?: string; page?: number; limit?: number };
        const qs = new URLSearchParams();
        if (p.search) qs.set('search', p.search);
        if (p.status) qs.set('status', p.status);
        if (p.plan) qs.set('plan', p.plan);
        if (p.page) qs.set('page', String(p.page));
        if (p.limit) qs.set('limit', String(p.limit));
        const q = qs.toString();
        return `/api/v1/platform/tenants${q ? `?${q}` : ''}`;
      },
      providesTags: ['PlatformTenant'],
    }),

    getPlatformTenant: builder.query<
      { success: boolean; data: PlatformTenantDetail },
      string
    >({
      query: (tenantId) => `/api/v1/platform/tenants/${tenantId}`,
      providesTags: (_r, _e, id) => [{ type: 'PlatformTenant', id }],
    }),

    updatePlatformTenantLifecycle: builder.mutation<
      { success: boolean },
      { tenantId: string; status: string; reason: string }
    >({
      query: ({ tenantId, status, reason }) => ({
        url: `/api/v1/platform/tenants/${tenantId}/lifecycle`,
        method: 'PUT',
        body: { status, reason },
      }),
      invalidatesTags: (_r, _e, { tenantId }) => [
        { type: 'PlatformTenant', id: tenantId },
        'PlatformTenant',
      ],
    }),

    updatePlatformTenantSubscription: builder.mutation<
      { success: boolean },
      { tenantId: string; plan_id: string; billing_cycle: string }
    >({
      query: ({ tenantId, ...body }) => ({
        url: `/api/v1/platform/tenants/${tenantId}/subscription`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { tenantId }) => [
        { type: 'PlatformTenant', id: tenantId },
        'PlatformTenant',
      ],
    }),

    clonePlatformTenant: builder.mutation<
      { success: boolean; data: CloneTenantResult },
      { tenantId: string; body: CloneTenantRequest }
    >({
      query: ({ tenantId, body }) => ({
        url: `/api/v1/platform/tenants/${tenantId}/clone`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PlatformTenant'],
    }),

    // ── Users ────────────────────────────────────────────────────────────────
    listPlatformUsers: builder.query<
      { success: boolean; data: PlatformUser[]; total: number },
      { search?: string; tenant_id?: string; status?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        const p = (params ?? {}) as { search?: string; tenant_id?: string; status?: string; page?: number; limit?: number };
        const qs = new URLSearchParams();
        if (p.search) qs.set('search', p.search);
        if (p.tenant_id) qs.set('tenant_id', p.tenant_id);
        if (p.status) qs.set('status', p.status);
        if (p.page) qs.set('page', String(p.page));
        if (p.limit) qs.set('limit', String(p.limit));
        const q = qs.toString();
        return `/api/v1/platform/users${q ? `?${q}` : ''}`;
      },
      providesTags: ['PlatformUser'],
    }),

    getPlatformUser: builder.query<
      { success: boolean; data: PlatformUserDetail },
      string
    >({
      query: (userId) => `/api/v1/platform/users/${userId}`,
      providesTags: (_r, _e, id) => [{ type: 'PlatformUser', id }],
    }),

    createPlatformUser: builder.mutation<
      { success: boolean; data: { user_id: string } },
      CreatePlatformUserRequest
    >({
      query: (body) => ({ url: '/api/v1/platform/users', method: 'POST', body }),
      invalidatesTags: ['PlatformUser'],
    }),

    updatePlatformUser: builder.mutation<
      { success: boolean },
      { userId: string; data: UpdatePlatformUserRequest }
    >({
      query: ({ userId, data }) => ({ url: `/api/v1/platform/users/${userId}`, method: 'PUT', body: data }),
      invalidatesTags: (_r, _e, { userId }) => ['PlatformUser', { type: 'PlatformUser', id: userId }],
    }),

    deletePlatformUser: builder.mutation<
      { success: boolean },
      string
    >({
      query: (userId) => ({ url: `/api/v1/platform/users/${userId}`, method: 'DELETE' }),
      invalidatesTags: ['PlatformUser'],
    }),

    updatePlatformUserRoles: builder.mutation<
      { success: boolean },
      { userId: string; data: UpdateUserRolesRequest }
    >({
      query: ({ userId, data }) => ({ url: `/api/v1/platform/users/${userId}/roles`, method: 'PUT', body: data }),
      invalidatesTags: (_r, _e, { userId }) => ['PlatformUser', { type: 'PlatformUser', id: userId }],
    }),

    updatePlatformMembershipStatus: builder.mutation<
      { success: boolean },
      { userId: string; tenantId: string; status: string }
    >({
      query: ({ userId, tenantId, status }) => ({
        url: `/api/v1/platform/users/${userId}/tenant-membership/${tenantId}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (_r, _e, { userId }) => ['PlatformUser', { type: 'PlatformUser', id: userId }],
    }),

    updatePlatformUserStatus: builder.mutation<
      { success: boolean },
      { userId: string; status: string }
    >({
      query: ({ userId, status }) => ({
        url: `/api/v1/platform/users/${userId}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['PlatformUser'],
    }),

    // ── Plans ────────────────────────────────────────────────────────────────
    listPlatformPlans: builder.query<
      { success: boolean; data: PlatformPlan[] },
      void
    >({
      query: () => '/api/v1/subscriptions/plans',
      providesTags: ['PlatformPlan'],
    }),

    createPlatformPlan: builder.mutation<
      { success: boolean; data: PlatformPlan },
      CreatePlanRequest
    >({
      query: (body) => ({ url: '/api/v1/subscriptions/plans', method: 'POST', body }),
      invalidatesTags: ['PlatformPlan'],
    }),

    updatePlatformPlan: builder.mutation<
      { success: boolean },
      { planId: string; data: UpdatePlanRequest }
    >({
      query: ({ planId, data }) => ({ url: `/api/v1/subscriptions/plans/${planId}`, method: 'PUT', body: data }),
      invalidatesTags: ['PlatformPlan'],
    }),

    deletePlatformPlan: builder.mutation<
      { success: boolean },
      string
    >({
      query: (planId) => ({ url: `/api/v1/subscriptions/plans/${planId}`, method: 'DELETE' }),
      invalidatesTags: ['PlatformPlan'],
    }),

    // ── Configurations (platform catalogue) ─────────────────────────────────
    listCatalogueEntries: builder.query<
      { success: boolean; data: CatalogueEntry[] },
      { prefix?: string; scopeTenantId?: string; scopeRoleId?: string; scopeUserId?: string } | void
    >({
      query: (params) => {
        const p = (params ?? {}) as { prefix?: string; scopeTenantId?: string; scopeRoleId?: string; scopeUserId?: string };
        const qs = new URLSearchParams();
        if (p.prefix) qs.set('prefix', p.prefix);
        if (p.scopeTenantId) qs.set('scope_tenant_id', p.scopeTenantId);
        if (p.scopeRoleId) qs.set('scope_role_id', p.scopeRoleId);
        if (p.scopeUserId) qs.set('scope_user_id', p.scopeUserId);
        const q = qs.toString();
        return `/api/v1/configurations/catalogue${q ? `?${q}` : ''}`;
      },
      providesTags: ['PlatformConfig'],
    }),

    updateCatalogueConfig: builder.mutation<
      { success: boolean; message_code: number },
      { key: string; value: Record<string, unknown>; scopeTenantId?: string; scopeRoleId?: string; scopeUserId?: string }
    >({
      query: ({ key, value, scopeTenantId, scopeRoleId, scopeUserId }) => {
        const qs = new URLSearchParams();
        if (scopeTenantId) qs.set('scope_tenant_id', scopeTenantId);
        if (scopeRoleId) qs.set('scope_role_id', scopeRoleId);
        if (scopeUserId) qs.set('scope_user_id', scopeUserId);
        const q = qs.toString();
        return { url: `/api/v1/configurations/${key}${q ? `?${q}` : ''}`, method: 'PUT', body: { value } };
      },
      invalidatesTags: ['PlatformConfig'],
    }),

    deleteCatalogueConfig: builder.mutation<
      { success: boolean; message_code: number },
      { key: string; scopeTenantId: string; scopeRoleId?: string; scopeUserId?: string }
    >({
      query: ({ key, scopeTenantId, scopeRoleId, scopeUserId }) => {
        const qs = new URLSearchParams();
        qs.set('scope_tenant_id', scopeTenantId);
        if (scopeRoleId) qs.set('scope_role_id', scopeRoleId);
        if (scopeUserId) qs.set('scope_user_id', scopeUserId);
        return { url: `/api/v1/configurations/${key}?${qs.toString()}`, method: 'DELETE' };
      },
      invalidatesTags: ['PlatformConfig'],
    }),

    // ── Platform roles (scoped to a tenant) ──────────────────────────────────
    listPlatformRoles: builder.query<
      { success: boolean; data: { role_id: string; name: string }[]; total: number },
      { tenantId: string }
    >({
      query: ({ tenantId }) => `/api/v1/platform/roles?tenant_id=${tenantId}`,
    }),

    // ── One configuration entry (all layers; the rulebook key also carries `editor_schema`) ──
    getConfigurationEntry: builder.query<{ success: boolean; data: ConfigurationEntry }, string>({
      query: (key) => `/api/v1/configurations/${key}`,
      providesTags: ['PlatformConfig'],
    }),

    // ── SAM prompt versions (also the SAM rulebook's save history: key=sam_cards.presentation_policy, setting=policy) ──
    getPromptVersions: builder.query<
      { success: boolean; data: PromptVersion[]; total: number },
      { setting: string; key?: string; limit?: number }
    >({
      query: ({ setting, key = 'onboarding_agent_prompts', limit = 10 }) => {
        const qs = new URLSearchParams({ setting, key, limit: String(limit) });
        return `/api/v1/platform/sam-prompts/versions?${qs}`;
      },
      providesTags: ['PlatformConfig'],
    }),

    // ── Agents with editable prompt catalogues (prompt-editor dropdown) ────────
    listAgentPrompts: builder.query<
      { success: boolean; data: AgentPromptCatalogue[]; total: number },
      void
    >({
      query: () => `/api/v1/platform/agent-prompts`,
    }),

    // ── Agent session logs ───────────────────────────────────────────────────
    listAgentSessions: builder.query<
      { success: boolean; data: AgentSessionSummary[]; total: number },
      { tenantId?: string; agentType?: string; userId?: string; search?: string; sortBy?: string; page?: number; limit?: number }
    >({
      query: ({ tenantId, agentType, userId, search, sortBy, page = 1, limit = 50 }) => {
        const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (tenantId) qs.set('tenant_id', tenantId);
        if (agentType) qs.set('agent_type', agentType);
        if (userId) qs.set('user_id', userId);
        if (search) qs.set('search', search);
        if (sortBy) qs.set('sort_by', sortBy);
        return `/api/v1/platform/agent-sessions?${qs}`;
      },
    }),

    getAgentSession: builder.query<
      { success: boolean; data: AgentSessionDetail },
      string
    >({
      query: (sessionId) => `/api/v1/platform/agent-sessions/${sessionId}`,
    }),

    // ── Chat Monitor ─────────────────────────────────────────────────────────
    listPlatformChatFeedback: builder.query<
      { success: boolean; data: ChatFeedbackEntry[]; total: number },
      { tenantId?: string; helpful?: boolean; limit?: number; offset?: number }
    >({
      query: ({ tenantId, helpful, limit = 50, offset = 0 }) => {
        const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (tenantId) qs.set('tenant_id', tenantId);
        if (helpful !== undefined) qs.set('helpful', String(helpful));
        return `/api/v1/platform/chat/feedback?${qs}`;
      },
    }),

    getPlatformChatConversation: builder.query<
      { success: boolean; data: PlatformConversation },
      { conversationId: string; messageId?: string | null; limit?: number; offset?: number }
    >({
      query: ({ conversationId, messageId, limit, offset }) => {
        const qs = new URLSearchParams();
        if (messageId) qs.set('message_id', messageId);
        if (limit != null) qs.set('limit', String(limit));
        if (offset != null) qs.set('offset', String(offset));
        const q = qs.toString();
        return `/api/v1/platform/chat/conversations/${conversationId}${q ? `?${q}` : ''}`;
      },
    }),

    getPlatformConversationRuns: builder.query<
      { success: boolean; data: AgentRun[]; total: number },
      { conversationId: string; turnId?: string }
    >({
      query: ({ conversationId, turnId }) =>
        `/api/v1/platform/chat/conversations/${conversationId}/runs${turnId ? `?turn_id=${encodeURIComponent(turnId)}` : ''}`,
    }),

    getAiBilling: builder.query<{ success: boolean; data: AiBillingReport }, AiBillingArgs>({
      query: (params) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
        return `/api/v1/platform/ai-billing?${qs.toString()}`;
      },
    }),

    // ── Usage (token usage as reported by the providers, aggregated from conversation_runs) ──
    getPlatformUsage: builder.query<
      { success: boolean; data: UsageResponse; total: number },
      { group_by: UsageGroupBy; split?: 'vendor_model' } & UsageFilters
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
        return `/api/v1/platform/usage?${qs.toString()}`;
      },
    }),

    listLlmPrices: builder.query<
      { success: boolean; data: { prices: LlmPrice[]; vendors: string[]; unpriced_models: { vendor: string; model: string; runs: number }[] } },
      void
    >({
      query: () => '/api/v1/platform/pricing',
      providesTags: ['LlmPricing'],
    }),
    addLlmPrice: builder.mutation<{ success: boolean }, LlmPriceInput>({
      query: (body) => ({ url: '/api/v1/platform/pricing', method: 'POST', body }),
      invalidatesTags: ['LlmPricing'],
    }),
    deleteLlmPrice: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/api/v1/platform/pricing/${id}`, method: 'DELETE' }),
      invalidatesTags: ['LlmPricing'],
    }),

    listLlmModels: builder.query<
      { success: boolean; data: { model_id: string; name: string; description?: string; status: string }[] },
      void
    >({
      query: () => `/api/v1/agents/models`,
    }),

    analyzeConversationTurn: builder.mutation<
      { success: boolean; data: { analysis: string; model_id: string; context: string } },
      { conversationId: string; messageId: string; modelId: string; userPrompt?: string | null }
    >({
      query: ({ conversationId, messageId, modelId, userPrompt }) => ({
        url: `/api/v1/platform/chat/conversations/${conversationId}/analyze`,
        method: 'POST',
        body: { message_id: messageId, model_id: modelId, user_prompt: userPrompt ?? null },
      }),
    }),
  }),
});

export const {
  useListPlatformTenantsQuery,
  useGetPlatformTenantQuery,
  useUpdatePlatformTenantLifecycleMutation,
  useUpdatePlatformTenantSubscriptionMutation,
  useClonePlatformTenantMutation,
  useListPlatformUsersQuery,
  useGetPlatformUserQuery,
  useCreatePlatformUserMutation,
  useUpdatePlatformUserMutation,
  useDeletePlatformUserMutation,
  useUpdatePlatformUserRolesMutation,
  useUpdatePlatformMembershipStatusMutation,
  useUpdatePlatformUserStatusMutation,
  useListPlatformPlansQuery,
  useCreatePlatformPlanMutation,
  useUpdatePlatformPlanMutation,
  useDeletePlatformPlanMutation,
  useListCatalogueEntriesQuery,
  useUpdateCatalogueConfigMutation,
  useDeleteCatalogueConfigMutation,
  useListPlatformRolesQuery,
  useGetPromptVersionsQuery,
  useGetConfigurationEntryQuery,
  useListAgentPromptsQuery,
  useListAgentSessionsQuery,
  useGetAgentSessionQuery,
  useListPlatformChatFeedbackQuery,
  useGetPlatformChatConversationQuery,
  useGetPlatformConversationRunsQuery,
  useListLlmModelsQuery,
  useGetPlatformUsageQuery,
  useGetAiBillingQuery,
  useListLlmPricesQuery,
  useAddLlmPriceMutation,
  useDeleteLlmPriceMutation,
  useLazyGetAiBillingQuery,
  useAnalyzeConversationTurnMutation,
} = platformAdminApi;
